import { lazy, Suspense } from 'react'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Lazy load PDFViewer to avoid pdfjs-dist ESM issues with Rspack
const PDFViewer = lazy(() => import('@/components/PDFViewer'))
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { clearRegulationsCache } from '@/modules/Regulations/hooks/useRegulationsCheck'
import { acceptRegulations } from '@/modules/Regulations/services'
import { RegulationsConfig } from '@/modules/Regulations/types'
import useAuthStore from '@/stores/useAuthStore'
import { useCallback, useEffect, useId, useState } from 'react'
import React from 'react'
import { toast } from '@vnetwork/datacentral-ui/feedback'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import {
    Checkbox,
    Text,
    Button,
    IconButton,
    Label,
    Spinner,
} from '@vnetwork/datacentral-ui/primitives'

interface RegulationsModalProps {
    regulation: RegulationsConfig
}

const RegulationsModal: React.FC<RegulationsModalProps> = ({ regulation }) => {
    const { t } = useTranslation()
    const { setEmployee, employee } = useAuthStore()
    const { getPresignedUrl } = useFilePreviewWithPresigned()

    const pdfAttachment = regulation.attachments?.find(
        (att) => att.attachment_file_type === 'application/pdf'
    )
    const pdfPath = pdfAttachment?.attachment_file_url

    const [presignedUrl, setPresignedUrl] = useState<string | null>(null)
    const [isLoadingUrl, setIsLoadingUrl] = useState(false)
    const [hasViewedAllPages, setHasViewedAllPages] = useState(false)
    const [hasAgreed, setHasAgreed] = useState(false)
    const agreeId = useId()
    // Mobile: summary HIỆN mặc định (như bình thường), bấm ▲ để thu gọn lấy chỗ cho PDF
    // (desktop luôn hiện đầy đủ, không có nút)
    const [summaryExpanded, setSummaryExpanded] = useState(true)

    // Fetch presigned URL when pdfPath is available
    useEffect(() => {
        if (pdfPath && !presignedUrl && !isLoadingUrl) {
            setIsLoadingUrl(true)
            getPresignedUrl(pdfPath)
                .then((url) => {
                    setPresignedUrl(url ?? null)
                })
                .finally(() => {
                    setIsLoadingUrl(false)
                })
        }
    }, [pdfPath])

    const handleAllPagesViewed = useCallback(() => {
        setHasViewedAllPages(true)
    }, [])

    const { mutate: submitAcceptance, isPending } = useMutationWithHandler(
        {
            mutationFn: () =>
                acceptRegulations({
                    regulation_version: regulation.regulation_version,
                }),
            onSuccess: () => {
                // Clear old cache so new status will be fetched and cached
                clearRegulationsCache()

                if (employee) {
                    setEmployee({
                        ...employee,
                        regulations_accepted_version:
                            regulation.regulation_version,
                    })
                }
            },
            onError: () => {
                toast.error(t('regulations:regulations-accepted-error'))
            },
        },
        {
            reactQueryKeys: [QUERY_KEYS.GENERIC, 'regulations.stats'],
        }
    )

    const canSubmit = hasViewedAllPages && hasAgreed && !isPending

    // Full-screen, non-dismissable acceptance gate (kept as a fixed overlay on
    // purpose: DS Dialog is dismissable and caps height below the viewport).
    const centered = (node: React.ReactNode) => (
        <div className="flex h-full items-center justify-center">{node}</div>
    )

    // No PDF attachment found
    if (!pdfPath) {
        return (
            <div className="fixed inset-0 z-[var(--vn-z-modal)] flex flex-col items-center justify-center bg-surface">
                <Text tone="danger">
                    {t('regulations:regulation-file-not-found')}
                </Text>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-[var(--vn-z-modal)] flex flex-col bg-surface">
            {/* Header */}
            <header className="flex flex-shrink-0 items-center gap-4 border-b border-border px-4 py-2">
                <img
                    src="/images/icon.png"
                    alt="VNETWORK"
                    className="h-10 w-auto"
                />
                <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                        <Text
                            as="div"
                            className="truncate md:overflow-visible md:whitespace-normal"
                            size="lg"
                            weight="semibold"
                        >
                            {regulation?.regulation_title ||
                                t('regulations:company-regulations-title')}
                        </Text>
                        {regulation.regulation_summary && (
                            <IconButton
                                size="sm"
                                onClick={() => setSummaryExpanded((v) => !v)}
                                aria-label={t(
                                    'regulations:company-regulations-title'
                                )}
                                aria-expanded={summaryExpanded}
                                className="flex-shrink-0 md:hidden"
                            >
                                {summaryExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                ) : (
                                    <ChevronDown className="h-4 w-4" />
                                )}
                            </IconButton>
                        )}
                    </div>
                    {regulation.regulation_summary && (
                        <Text
                            as="p"
                            size="sm"
                            tone="muted"
                            className={`${summaryExpanded ? 'block' : 'hidden'} md:block`}
                        >
                            {regulation?.regulation_summary}
                        </Text>
                    )}
                </div>
            </header>

            {/* PDF Content */}
            <div className="flex-1 overflow-hidden">
                {isLoadingUrl || (!presignedUrl && pdfPath) ? (
                    centered(<Spinner size="lg" />)
                ) : presignedUrl ? (
                    <Suspense fallback={centered(<Spinner size="lg" />)}>
                        <PDFViewer
                            url={presignedUrl}
                            onAllPagesViewed={handleAllPagesViewed}
                            disableDownload
                        />
                    </Suspense>
                ) : (
                    centered(
                        <Text tone="danger">{t('error-loading-pdf-file')}</Text>
                    )
                )}
            </div>

            {/* Footer */}
            <footer className="flex-shrink-0 border-t border-border px-4 py-2">
                <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id={agreeId}
                            checked={hasAgreed}
                            onCheckedChange={(checked) =>
                                setHasAgreed(checked === true)
                            }
                            disabled={!hasViewedAllPages}
                        />
                        <Label
                            htmlFor={agreeId}
                            className={`cursor-pointer select-none ${!hasViewedAllPages ? 'text-muted-foreground' : ''}`}
                        >
                            {t('regulations:regulations-agreement-text')}
                            {!hasViewedAllPages && (
                                <Text size="xs" tone="warning">
                                    {' '}
                                    (
                                    {t(
                                        'regulations:regulations-scroll-all-pages-hint'
                                    )}
                                    )
                                </Text>
                            )}
                        </Label>
                    </div>
                    <Button
                        onClick={() => submitAcceptance()}
                        disabled={!canSubmit}
                        loading={isPending}
                    >
                        {t('regulations:accept-regulations')}
                    </Button>
                </div>
            </footer>
        </div>
    )
}

export default RegulationsModal
